import type {
  AttainmentTrendPeriod,
  TeammateAttainmentTrends,
} from "@repo/app-types/entities/attainmentTrend";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type Teammate,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { recordEntries } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";

import {
  getCriteriaTrendMovementCategory,
  type CriteriaAttainmentTrendMovementCategory,
} from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { appPaths } from "../../app/appPaths.js";
import {
  computeScoreAxisRange,
  formatScore,
  formatScoreAxisLabel,
  formatScoreDelta,
  MOVEMENT_COLORS,
  ScoreMovementScatterChart,
  type ScoreMovementPoint,
} from "./scoreMovementScatterChart.js";

interface TeammateScoreMovement {
  id: string;
  name: string;
  startScore: number;
  currentScore: number;
  delta: number;
}

const PERIOD_OPTIONS: {
  value: AttainmentTrendPeriod;
  label: string;
}[] = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
  { value: "last_180_days", label: "Last 180 days" },
];

interface CriteriaAttainmentTrendChartProps {
  attainmentTrend: Record<TeammateId, TeammateAttainmentTrends>;
  teammates: readonly Teammate[];
  playbookId: PlaybookId;
  criterionId: string;
  gradingValueType: "yes_no" | "1_5_score";
  onSelectedPeriodChange?: (period: AttainmentTrendPeriod) => void;
}

export const CriteriaAttainmentTrendChart: FC<
  CriteriaAttainmentTrendChartProps
> = ({
  attainmentTrend,
  teammates,
  playbookId,
  criterionId,
  gradingValueType,
  onSelectedPeriodChange,
}) => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] =
    useState<AttainmentTrendPeriod>("last_30_days");
  const handleSelectedPeriodChange = (period: AttainmentTrendPeriod) => {
    setSelectedPeriod(period);
    onSelectedPeriodChange?.(period);
  };
  const [visibleCategories, setVisibleCategories] = useState<
    Record<CriteriaAttainmentTrendMovementCategory, boolean>
  >({
    improving: true,
    flat: true,
    declining: true,
  });

  const toggleCategory = (
    category: CriteriaAttainmentTrendMovementCategory,
  ) => {
    setVisibleCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Build map from teammates array
  const teammatesMap = useMemo(() => {
    return teammates.reduce<Record<TeammateId, Teammate>>((acc, teammate) => {
      acc[teammate.id] = teammate;
      return acc;
    }, {});
  }, [teammates]);

  // Format average delta for legend button
  const formatAvgDelta = (avgDelta: number): string => {
    if (gradingValueType === "yes_no") {
      return `${avgDelta >= 0 ? "+" : ""}${Math.round(avgDelta * 100)} pts avg`;
    }
    return `${avgDelta >= 0 ? "+" : ""}${avgDelta.toFixed(1)} avg`;
  };

  // Transform attainment trend into TeammateScoreMovement array
  const teammateScoreMovements =
    useMemo((): readonly TeammateScoreMovement[] => {
      const result: TeammateScoreMovement[] = [];

      for (const [teammateId, trends] of recordEntries(attainmentTrend)) {
        const period = trends[selectedPeriod];

        // Skip if this period doesn't exist in the data (e.g., old pre-computed data)
        if (!period) {
          continue;
        }

        const startScore = period.starting_score;
        const currentScore = period.current_score;

        // Skip teammates with missing scores
        if (startScore === null || currentScore === null) {
          continue;
        }

        // Get display name from teammatesMap if available
        const teammate = teammatesMap[teammateId];
        const displayName = teammate
          ? buildTeammateDisplayNames(teammate).primaryDisplayName
          : teammateId;

        result.push({
          id: teammateId,
          name: displayName,
          startScore,
          currentScore,
          delta: currentScore - startScore,
        });
      }

      return result;
    }, [attainmentTrend, selectedPeriod, teammatesMap]);

  // Calculate category stats for legend buttons
  const categoryStats = useMemo(() => {
    const stats = {
      improving: { count: 0, totalDelta: 0 },
      flat: { count: 0, totalDelta: 0 },
      declining: { count: 0, totalDelta: 0 },
    };

    for (const t of teammateScoreMovements) {
      const category = getCriteriaTrendMovementCategory(
        t.delta,
        gradingValueType,
      );
      stats[category].count++;
      stats[category].totalDelta += t.delta;
    }

    return {
      improving: {
        count: stats.improving.count,
        avgDelta:
          stats.improving.count > 0
            ? stats.improving.totalDelta / stats.improving.count
            : 0,
      },
      flat: {
        count: stats.flat.count,
      },
      declining: {
        count: stats.declining.count,
        avgDelta:
          stats.declining.count > 0
            ? stats.declining.totalDelta / stats.declining.count
            : 0,
      },
    };
  }, [teammateScoreMovements, gradingValueType]);

  // Calculate dynamic axis range based on data and grading type
  const { axisMin, axisMax, axisTicks } = useMemo(
    () => computeScoreAxisRange(teammateScoreMovements, gradingValueType),
    [teammateScoreMovements, gradingValueType],
  );

  // Build chart points, filtering by visible categories
  const chartPoints = useMemo((): readonly ScoreMovementPoint[] => {
    return teammateScoreMovements
      .map((teammate): ScoreMovementPoint | null => {
        const category = getCriteriaTrendMovementCategory(
          teammate.delta,
          gradingValueType,
        );

        if (!visibleCategories[category]) {
          return null;
        }

        return {
          id: teammate.id,
          startScore: teammate.startScore,
          currentScore: teammate.currentScore,
          category,
          tooltipContent: `${teammate.name}\nBefore: ${formatScore(teammate.startScore, gradingValueType)}\nAfter: ${formatScore(teammate.currentScore, gradingValueType)}\nDelta: ${formatScoreDelta(teammate.delta, gradingValueType)}`,
          onClick: () =>
            navigate(
              appPaths.teammateCriterion.build({
                playbookId,
                teammateId: teammate.id,
                criterionId,
              }),
            ),
        };
      })
      .filter((p): p is ScoreMovementPoint => p !== null);
  }, [
    teammateScoreMovements,
    visibleCategories,
    gradingValueType,
    navigate,
    playbookId,
    criterionId,
  ]);

  return (
    <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
      <Label size={Size.SMALL} className="mb-2">
        Criteria attainment trend
      </Label>

      {/* Header with filter buttons and period dropdown */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-2">
          {/* Improving button */}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => toggleCategory("improving")}
            className={twMerge(
              "font-normal transition-opacity",
              !visibleCategories.improving && "opacity-50",
            )}
          >
            <span
              className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: MOVEMENT_COLORS.improving }}
            />
            <span>
              {categoryStats.improving.count} improved
              {categoryStats.improving.count > 0 &&
                ` (${formatAvgDelta(categoryStats.improving.avgDelta)})`}
            </span>
          </Button>

          {/* Flat button */}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => toggleCategory("flat")}
            className={twMerge(
              "font-normal transition-opacity",
              !visibleCategories.flat && "opacity-50",
            )}
          >
            <span
              className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: MOVEMENT_COLORS.flat }}
            />
            <span>{categoryStats.flat.count} flat</span>
          </Button>

          {/* Declining button */}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => toggleCategory("declining")}
            className={twMerge(
              "font-normal transition-opacity",
              !visibleCategories.declining && "opacity-50",
            )}
          >
            <span
              className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: MOVEMENT_COLORS.declining }}
            />
            <span>
              {categoryStats.declining.count} declined
              {categoryStats.declining.count > 0 &&
                ` (${formatAvgDelta(categoryStats.declining.avgDelta)})`}
            </span>
          </Button>
        </div>

        <SimpleSelect
          label="Period"
          isLabelHidden
          options={PERIOD_OPTIONS}
          value={selectedPeriod}
          onChange={handleSelectedPeriodChange}
        />
      </div>

      {/* Chart */}
      <ScoreMovementScatterChart
        points={chartPoints}
        axisMin={axisMin}
        axisMax={axisMax}
        axisTicks={axisTicks}
        formatAxisLabel={(tick) => formatScoreAxisLabel(tick, gradingValueType)}
        xAxisTitle="Starting score"
        yAxisTitle="Current score"
      />
    </div>
  );
};
